if (!customElements.get('language-switcher')) {
  class LanguageSwitcher extends HTMLElement {
    constructor() {
      super();

      // Session storage key for tracking language choice
      this.SESSION_KEY = 'languageChosen';

      // Bound event handlers for cleanup
      this.boundHandlers = {
        countryChange: this.onCountryChange.bind(this),
        formSubmit: this.onFormSubmit.bind(this),
        modalClose: this.onModalClose.bind(this),
        modalBackdropClick: this.onModalBackdropClick.bind(this),
      };
    }

    connectedCallback() {
      // Find DOM elements after connection
      this.countrySelect = this.querySelector('[data-country-select]');
      this.languageSelect = this.querySelector('[data-language-select]');
      this.modal = this.querySelector('.modal');
      this.modalTrigger = this.querySelector('[data-modal-trigger]');
      this.closeButton = this.querySelector('[data-close-modal]');
      this.form = this.querySelector('form');

      this.currentCountryCode = this.dataset.currentCountryCode;
      this.currentLanguageCode = this.dataset.currentLanguageCode;

      // Check if this is a functional language switcher with selects
      const hasSelects = this.countrySelect && this.languageSelect;

      if (hasSelects) {
        // Set up event listeners for select elements
        this.setupEventListeners();

        // Initialize language options
        const initialOption = this.countrySelect?.selectedOptions[0];
        if (initialOption) {
          const languages = this._parseLanguages(initialOption.dataset.languages);
          this.updateLanguageOptions(languages, this.currentLanguageCode);
        }
      } else {
        // Still set up basic event listeners (form submit, modal close)
        this.setupBasicEventListeners();
      }

      // Always check if we need to auto-open the language selector (regardless of selects)
      this.checkAndAutoOpen();
    }

    disconnectedCallback() {
      // Clean up event listeners
      this.countrySelect?.removeEventListener('change', this.boundHandlers.countryChange);
      this.form?.removeEventListener('submit', this.boundHandlers.formSubmit);
      this.closeButton?.removeEventListener('click', this.boundHandlers.modalClose);
      this.modal?.removeEventListener('click', this.boundHandlers.modalBackdropClick);
    }

    setupEventListeners() {
      // Country selection change
      if (this.countrySelect) {
        this.countrySelect.addEventListener('change', this.boundHandlers.countryChange);
      }

      // Set up basic listeners (form, modal)
      this.setupBasicEventListeners();
    }

    setupBasicEventListeners() {
      // Form submission (language choice)
      if (this.form) {
        this.form.addEventListener('submit', this.boundHandlers.formSubmit);
      }

      // Modal close button
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.boundHandlers.modalClose);
      }

      // Modal backdrop click
      if (this.modal) {
        this.modal.addEventListener('click', this.boundHandlers.modalBackdropClick);
      }
    }

    checkAndAutoOpen() {
      // Check if user has already made a language choice in this session
      const hasChosen = sessionStorage.getItem(this.SESSION_KEY);

      if (!hasChosen && this.modal) {
        // Auto-open the language selector modal
        setTimeout(() => {
          this.openModal();
        }, 100); // Small delay to ensure proper rendering
      }
    }

    openModal() {
      if (this.modal) {
        this.modal.classList.remove('hidden');
        this.modal.classList.add('flex');

        // Focus management for accessibility
        const firstInput = this.modal.querySelector('select, button');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }

    closeModal() {
      if (this.modal) {
        this.modal.classList.add('hidden');
        this.modal.classList.remove('flex');

        // Mark that user has made a choice (even if just closing)
        this.markLanguageChosen();
      }
    }

    markLanguageChosen() {
      // Set session storage to prevent showing modal again this session
      sessionStorage.setItem(this.SESSION_KEY, 'true');
    }

    onFormSubmit() {
      // User actively selected a language
      this.markLanguageChosen();
      // Form will handle the actual submission and page redirect
    }

    onModalClose(event) {
      event.preventDefault();
      this.closeModal();
    }

    onModalBackdropClick(event) {
      // Only close if clicking the backdrop (modal itself), not its contents
      if (event.target === this.modal) {
        this.boundHandlers.modalClose(event);
      }
    }

    onCountryChange() {
      const option = this.countrySelect?.selectedOptions[0];
      if (!option) return;

      const languages = this._parseLanguages(option.dataset.languages);
      if (!languages.length) return;

      let targetLanguage = null;
      const previousLanguage = this.languageSelect?.value;

      if (previousLanguage) {
        const isAvailable = languages.some((lang) => lang.iso_code === previousLanguage);
        if (isAvailable) {
          targetLanguage = previousLanguage;
        }
      }

      this.updateLanguageOptions(languages, targetLanguage || languages[0].iso_code);
    }

    _parseLanguages(str) {
      if (!str) return [];

      const cleaned = str.endsWith('|') ? str.slice(0, -1) : str;
      if (!cleaned) return [];

      return cleaned
        .split('|')
        .map((pair) => {
          const [code, name] = pair.split(',').map((s) => s.trim());
          return code && name ? { iso_code: code, endonym_name: name } : null;
        })
        .filter(Boolean);
    }

    updateLanguageOptions(languages, activeCode) {
      if (!languages.length || !this.languageSelect) return;

      const dropdown = this.languageSelect.querySelector('.custom-select__dropdown');
      if (!dropdown) return;

      const template = document.getElementById('language-option-template');
      if (!template) return;

      dropdown.innerHTML = '';

      languages.forEach((lang) => {
        const optionElement = template.content.cloneNode(true);
        const option = optionElement.querySelector('.custom-select__option');
        const nameSpan = optionElement.querySelector('.language-name');

        option.setAttribute('data-value', lang.iso_code);
        nameSpan.textContent = lang.endonym_name;

        if (lang.iso_code === activeCode) {
          option.setAttribute('aria-selected', 'true');
        } else {
          option.setAttribute('aria-selected', 'false');
        }

        dropdown.appendChild(optionElement);
      });

      // Re-initialize the custom-select component
      this.languageSelect.initializeElements();
      this.languageSelect.setupEventListeners();

      // Update the value and display
      if (activeCode) {
        this.languageSelect.value = activeCode;
      }
    }
  }

  window.LanguageSwitcher = LanguageSwitcher;
  customElements.define('language-switcher', LanguageSwitcher);
}
